/**
 * @file enums.ts
 * @description 全系統共用枚舉常數（mirrors DB enums）。
 * 所有 const array 均使用 `as const` 以保留 literal type，
 * 對應的 union type 由 `(typeof X)[number]` 推導。
 */
// ─── 使用者角色 ───────────────────────────────────────────────────────────────
/**
 * 系統使用者角色。
 * verification_lead（驗證組長，序59）：合約評審單「驗證組長意見 + 簽名」段簽署人；
 * 驗證組長本人與代理人（資深稽核員）皆賦予此角色即可登入簽名。
 * accountant（會計，序52）：只負責【報價】與【繳費】管理業務（見 can.ts 的
 * `case.quotation.manage` / `case.payment.manage`）。
 */
export const USER_ROLES = [
    'admin',
    'manager',
    'decider',
    'auditor',
    'verification_lead',
    'accountant',
];
/**
 * 系統使用者角色 → 中文顯示名稱。
 *
 * 用於所有面向使用者的角色顯示（表單下拉、表格欄、Pill 標籤等），
 * 確保整個系統角色文案一致。資料庫仍儲存英文 enum 值，僅 UI 層做 i18n 對應。
 */
export const USER_ROLE_LABELS = {
    admin: '系統管理員',
    manager: '管理組',
    decider: '驗證決定組',
    auditor: '稽核員',
    verification_lead: '驗證組長',
    accountant: '會計',
};
// ─── 驗證方案 ─────────────────────────────────────────────────────────────────
/** 支援的驗證方案代碼 */
export const VERIFICATION_SCHEMES = ['TOP', 'TAP', 'ECO'];
/** 驗證方案代碼 → 中文標籤。TOP=有機農產品驗證；TAP=產銷履歷；ECO=生態服務。 */
export const SCHEME_LABELS = {
    TOP: '有機農產品驗證（TOP）',
    TAP: '產銷履歷（TAP）',
    ECO: '生態服務（ECO）',
};
// ─── 驗證狀態 ─────────────────────────────────────────────────────────────────
/** 客戶驗證資格狀態（與 state machine 搭配使用） */
export const VERIFICATION_STATUSES = [
    '新申請',
    '維持',
    '結束驗證',
    '撤銷驗證',
    '暫時終止驗證_TOP',
    '暫時停止驗證_TAP',
    '恢復驗證',
    '終止驗證',
];
// ─── 費用狀態 ─────────────────────────────────────────────────────────────────
/** 客戶費用繳納狀態 */
export const PAYMENT_STATUSES = ['正常', '異常_暫終', '異常_終止驗證'];
// ─── 案件工作流 ───────────────────────────────────────────────────────────────
/** 驗證案件全流程狀態 */
export const CASE_WORKFLOW_STATES = [
    '受理建檔',
    '已派案',
    '已繳費',
    '稽核中',
    '報告中',
    '報告評審',
    '決定中',
    '已發證',
    '已寄送',
    '結案',
    'NC整改中',
];
// ─── 稽核任務狀態 ─────────────────────────────────────────────────────────────
/** 稽核任務執行狀態 */
export const AUDIT_TASK_STATUSES = [
    '待派案',
    '已派案',
    '已確認',
    '計畫中',
    '現場中',
    '報告中',
    '完成',
    '退回改派',
];
// ─── 稽核任務階段（流程化視圖） ───────────────────────────────────────────────
/**
 * 稽核任務生命週期六階段。
 * 對應任務詳情頁 Steps，每個階段下可承載一組子內容（查核表 / 採樣 / NC 等）。
 * 與 AUDIT_TASK_STATUSES（細粒度狀態）並存：階段是粗粒度的流程定位，
 * 狀態是當前任務的工作進度標記。
 */
export const AUDIT_TASK_PHASES = [
    'preparation', // 文件審查（序64/122 由「開案準備」改名）：接收案件 / 應附文件 / 文件審查記錄 / 客戶檔案
    'planning', // 稽核計劃：排程 / 表單清單 / 通知擬發
    'field', // 實地稽核（序70 由「現場稽核」再改名）：查核表 / 採樣 / NC / 拍照 / 打卡
    'lab', // 檢驗 / 資材：實驗室報告 / 有機資材審查
    'report', // 報告編製：稽核報告撰寫 / 評審
    'closing', // 結案：移交決定流程 / 歸檔
];
/**
 * 階段中文標籤（UI 顯示用）。
 * 客戶 2026-07（序134）名詞調整：稽核計劃→稽核計畫、實地稽核→現場實地稽核、
 * 產品抽驗檢驗→產品抽樣檢驗、報告編製→稽核報告編制、驗證決定/發證移交→提交審議。
 */
export const AUDIT_TASK_PHASE_LABELS = {
    preparation: '文件審查',
    planning: '稽核計畫',
    field: '現場實地稽核',
    lab: '產品抽樣檢驗',
    report: '稽核報告編制',
    closing: '提交審議',
};
// ─── 稽核任務類型（驅動表單預選） ─────────────────────────────────────────────
/**
 * 稽核任務類型（細分驗證活動性質）。
 * 與 form_templates.applicableTaskTypes 配合：創建任務時按此值匹配模板適用範圍。
 */
export const AUDIT_TASK_TYPES = [
    'initial', // 初次驗證
    'renewal', // 續證
    'surveillance', // 年度監督
    'special', // 特殊（追加 / 抽查 / 申訴複查）
];
export const AUDIT_TASK_TYPE_LABELS = {
    initial: '初次驗證',
    renewal: '續證',
    surveillance: '年度監督',
    special: '特殊稽核',
};
// ─── 決定委員會狀態 ───────────────────────────────────────────────────────────
/** 驗證決定委員會審議生命週期狀態（2026-07-02 重構：書面/會議審議 + 雙簽 + 總經理核定） */
export const DECISION_STATUSES = [
    '待安排',
    '審議中',
    '待總經理核定',
    '已決_通過',
    '已決_不通過',
    '已決_延議',
];
// ─── 證書狀態 ─────────────────────────────────────────────────────────────────
/** 驗證證書生命週期狀態 */
export const CERTIFICATE_STATUSES = [
    '製證中',
    '待寄送',
    '已寄送',
    '有效',
    '即將到期',
    '已到期',
    '暫停',
    '撤銷',
    '終止',
];
// ─── 背景工作狀態 ─────────────────────────────────────────────────────────────
/** 非同步/排程工作狀態 */
export const JOB_STATUSES = ['queued', 'running', 'done', 'failed'];
// ─── SLA 狀態 ─────────────────────────────────────────────────────────────────
/** 期限健康度顏色標示 */
export const SLA_STATUSES = ['green', 'yellow', 'red'];
// ─── 其他業務枚舉 ─────────────────────────────────────────────────────────────
/** 不符合項嚴重度 */
export const NC_SEVERITIES = ['重大', '一般', '觀察事項', '改善建議'];
/** 採樣類型 */
export const SAMPLING_TYPES = ['現場採樣', '年度後市場採樣', '質譜快檢'];
/** 稽核團隊成員角色 */
export const AUDIT_TEAM_ROLES = [
    '稽核組長',
    '稽核員',
    '觀察稽核員',
    '見習稽核員',
    '驗證助理',
];
// ─── 員工檔案枚舉 ─────────────────────────────────────────────────────────────
/** 性別 */
export const GENDERS = ['male', 'female', 'other'];
export const GENDER_LABELS = {
    male: '男',
    female: '女',
    other: '其他',
};
/** 聘用狀態 */
export const EMPLOYMENT_STATUSES = ['active', 'suspended', 'resigned'];
export const EMPLOYMENT_STATUS_LABELS = {
    active: '在職',
    suspended: '停聘',
    resigned: '離職',
};
// ─── 工作台類型（動態 RBAC） ──────────────────────────────────────────────────
/**
 * 工作台類型枚舉。
 * 決定自定義角色使用哪種佈局殼、工作台 Dashboard、菜單基準樹。
 * 動態 RBAC 配置新角色時必須選擇其中一個工作台類型。
 *
 * - admin：系統管理台（深色側邊欄，含全部管理功能）
 * - manager：管理工作台（含案件派遣/稽核管理等業務管理功能）
 * - decider：審議工作台（驗證決定委員會視角）
 * - auditor：稽核工作台（現場稽核員視角，支援離線現場模式）
 * - accountant：會計工作台（報價/繳費管理視角）
 */
export const WORKBENCH_TYPES = ['admin', 'manager', 'decider', 'auditor', 'accountant'];
/** 工作台類型 → 中文顯示名稱 */
export const WORKBENCH_TYPE_LABELS = {
    admin: '系統管理台',
    manager: '管理工作台',
    decider: '審議工作台',
    auditor: '稽核工作台',
    accountant: '會計工作台',
};
/**
 * 內置角色 → 工作台類型對照表。
 * 自定義角色的工作台類型存在 DB `roles.workbenchType` 欄位，不查此表。
 * verification_lead（驗證組長）使用管理工作台殼（manager），
 * 因其僅需合約評審簽核功能，不需完整 admin 介面。
 */
export const BUILTIN_ROLE_WORKBENCH = {
    admin: 'admin',
    manager: 'manager',
    decider: 'decider',
    auditor: 'auditor',
    verification_lead: 'manager',
    accountant: 'accountant',
};
